import type { ColumnDef } from "@tanstack/react-table";
import { TailSpin } from "react-loader-spinner";

import type { BaseRowItem } from "../../../components/data-table";
import { formatAmount } from "../../../utils/FormatPerson";

export const PAYROLL_STAFF_TABLE_LIMIT = 25;

function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function ordinalSuffix(day: number): string {
  switch (day) {
    case 1:
    case 21:
    case 31:
      return "st";
    case 2:
    case 22:
      return "nd";
    case 3:
    case 23:
      return "rd";
    default:
      return "th";
  }
}

function formatStaffDate(value: unknown): string {
  if (!value) return "—";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  return `${date.toLocaleString("default", {
    month: "short",
  })} ${day}${ordinalSuffix(day)}, ${date.getFullYear()}, ${date.toLocaleString(
    "default",
    { hour: "numeric", minute: "numeric", hour12: true }
  )}`;
}

export type PayrollStaffRecord = Record<string, unknown> & {
  _id?: string;
  fullName?: string;
  email?: string;
  role?: string;
  salary?: number;
  createdAt?: string;
};

export type PayrollStaffRow = BaseRowItem & {
  raw: PayrollStaffRecord;
  name: string;
  email: string;
  role: string;
  salary: string;
  dateCreated: string;
};

export function buildPayrollStaffRows(
  staffs: PayrollStaffRecord[]
): PayrollStaffRow[] {
  return staffs.map((staff, index) => ({
    id: str(staff._id) || `staff-${index}`,
    raw: staff,
    name: staff.fullName ? String(staff.fullName).toUpperCase() : "--- ---",
    email: str(staff.email) || "----",
    role: str(staff.role) || "----",
    salary: `₦ ${formatAmount(staff.salary) ?? "0.00"}`,
    dateCreated: formatStaffDate(staff.createdAt),
  }));
}

export function createPayrollStaffColumns(
  onView: (row: PayrollStaffRow) => void,
  onDelete: (row: PayrollStaffRow) => void,
  deletingId: string | null
): ColumnDef<PayrollStaffRow, unknown>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ getValue }) => (
        <span className="text-[14px] text-white">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      meta: { hideBelow: "sm" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#AEBCC6]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "role",
      header: "Role",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#E7EBEE]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "salary",
      header: "Salary",
      cell: ({ getValue }) => (
        <span className="tabular-nums text-[13px] text-white">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "dateCreated",
      header: "Date Created",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
    {
      id: "actions",
      header: "Action",
      meta: { align: "right" as const },
      cell: ({ row }) => {
        const isDeleting = deletingId === row.original.raw._id;
        return (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => onView(row.original)}
              className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button transition hover:border-secondary_button hover:bg-[#273037]"
            >
              View
            </button>
            <button
              type="button"
              disabled={isDeleting}
              onClick={() => onDelete(row.original)}
              className="flex items-center justify-center rounded-lg border border-gray-800 px-4 py-1.5 text-[12px] font-semibold text-[#DF4E4E] disabled:opacity-60"
            >
              {isDeleting ? (
                <TailSpin color="#FFFFFF" height={18} width={18} />
              ) : (
                "Delete"
              )}
            </button>
          </div>
        );
      },
    },
  ];
}

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildPayrollStaffCsv(staffs: PayrollStaffRecord[]): string {
  const header = ["Name", "Email", "Role", "Salary", "Date created"];
  const rows = staffs.map((s) => [
    csvCell(s.fullName ? String(s.fullName).toUpperCase() : ""),
    csvCell(str(s.email)),
    csvCell(str(s.role)),
    csvCell(`${formatAmount(s.salary) ?? "0.00"}`),
    csvCell(formatStaffDate(s.createdAt)),
  ]);
  return [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
}
